import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useUsersListQuery } from 'api';
import { GoBackButtonWithTitle, PageHeaderBoldTitle, PageWrapper, Spinner } from 'components';
import { useCreateDraft } from 'hooks';
import { EquipmentTabsValues, PostDraft, UserStatus } from 'types';

import { RegisterEquipmentForm, RequestEquipmentForm, TabPanel, Tabs } from '../../components';
import { createEquipmentsTabs } from '../../components/constants';
import { defaultValuesAdapter } from './adapter';
import { useEquipmentTabs } from './hooks';
import { ContentW, FormW } from './styled';

export function CreateEquipmentPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoading: isLoadingUsersList, usersList } = useUsersListQuery({
    status: [UserStatus.Active, UserStatus.Registered],
  });
  const { activeTab, handleChange } = useEquipmentTabs();
  const { draft, isLoading: isDraftLoading } = useCreateDraft();

  const defaultValues = (isDefaultEditable: boolean, draft?: PostDraft) =>
    defaultValuesAdapter({ isDefaultEditable, draft });

  if (isLoadingUsersList || isDraftLoading) {
    return <Spinner />;
  }

  return (
    <PageWrapper>
      <GoBackButtonWithTitle
        title={t('common.back')}
        onClick={() => navigate(-1)}
        containerSx={{
          marginTop: 0,
          maxWidth: '100%',
        }}
      />
      <PageHeaderBoldTitle title={t('equipments.create.title')} />
      <ContentW>
        <FormW>
          <Tabs activeTab={activeTab} tabs={createEquipmentsTabs} handleChange={handleChange} />
          <TabPanel path={EquipmentTabsValues.REGISTER} value={activeTab}>
            <RegisterEquipmentForm
              draft={draft}
              usersList={usersList}
              defaultValues={defaultValues}
            />
          </TabPanel>
          <TabPanel path={EquipmentTabsValues.REQUEST} value={activeTab}>
            <RequestEquipmentForm
              draft={draft}
              usersList={usersList}
              defaultValues={defaultValues}
            />
          </TabPanel>
        </FormW>
      </ContentW>
    </PageWrapper>
  );
}
